import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  CheckCircle, AlertTriangle, XCircle, Search, RefreshCw,
  Activity, Shield, Zap, Clock, ExternalLink, TrendingUp,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip,
  BarChart, Bar, Cell,
} from "recharts";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CFG = {
  operational: { color: "#10b981", label: "Operational", icon: CheckCircle },
  degraded: { color: "#f59e0b", label: "Degraded", icon: AlertTriangle },
  partial_outage: { color: "#f97316", label: "Partial Outage", icon: AlertTriangle },
  major_outage: { color: "#ef4444", label: "Major Outage", icon: XCircle },
};

function uptimeColor(pct) {
  if (pct >= 99.5) return "#10b981";
  if (pct >= 99) return "#34d399";
  if (pct >= 97) return "#fbbf24";
  if (pct >= 95) return "#f97316";
  return "#ef4444";
}

export default function ProviderStatusDashboard() {
  const [summary, setSummary] = useState(null);
  const [providers, setProviders] = useState([]);
  const [latency, setLatency] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [providerDetail, setProviderDetail] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [sRes, lRes, iRes] = await Promise.all([
        axios.get(`${API}/status/providers`),
        axios.get(`${API}/status/latency`),
        axios.get(`${API}/status/incidents?limit=15`),
      ]);
      setProviders(sRes.data.providers || []);
      setSummary(sRes.data.summary);
      setLatency(lRes.data.providers || []);
      setIncidents(iRes.data.incidents || []);
    } catch (e) {
      console.error("Status fetch failed:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 60000);
    return () => clearInterval(iv);
  }, [fetchAll]);

  const fetchDetail = async (slug) => {
    setSelectedProvider(slug);
    try {
      const res = await axios.get(`${API}/status/providers/${slug}?hours=48`);
      setProviderDetail(res.data);
    } catch (e) {
      console.error("Detail fetch failed:", e);
    }
  };

  const filtered = providers.filter(p =>
    p.provider_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[var(--qn-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="provider-status-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Provider Health Monitor</h2>
          <p className="text-sm text-[var(--qn-foreground-muted)]">Real-time status across all {summary?.total_providers || 69} AI providers</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/system-status" target="_blank" rel="noopener noreferrer" data-testid="open-public-status">
            <Button variant="outline" size="sm">
              <ExternalLink className="w-4 h-4 mr-1" /> Public Status
            </Button>
          </a>
          <Button variant="ghost" size="sm" onClick={() => { setRefreshing(true); fetchAll(); }} disabled={refreshing} data-testid="dashboard-refresh">
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[
            { label: "Total", val: summary.total_providers, icon: Activity, color: "#3b82f6" },
            { label: "Operational", val: summary.operational, icon: CheckCircle, color: "#10b981" },
            { label: "Degraded", val: summary.degraded, icon: AlertTriangle, color: "#f59e0b" },
            { label: "Outages", val: summary.partial_outage + summary.major_outage, icon: XCircle, color: "#ef4444" },
            { label: "Uptime", val: `${summary.uptime_pct}%`, icon: Shield, color: "#8b5cf6" },
          ].map((s, i) => (
            <Card key={s.label} className="border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid={`summary-${s.label.toLowerCase()}`}>
              <CardContent className="p-3 flex items-center gap-2">
                <s.icon className="w-4 h-4" style={{ color: s.color }} />
                <div>
                  <div className="text-lg font-bold font-mono">{s.val}</div>
                  <div className="text-[10px] text-[var(--qn-foreground-muted)]">{s.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-muted)]" />
        <Input
          placeholder="Filter providers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-9 bg-[var(--qn-background)] border-[var(--qn-border)]"
          data-testid="dashboard-provider-search"
        />
      </div>

      {/* Provider Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="provider-health-grid">
        {filtered.map((p, i) => {
          const cfg = STATUS_CFG[p.status] || STATUS_CFG.operational;
          const Icon = cfg.icon;
          const isSelected = selectedProvider === p.provider_slug;
          return (
            <motion.div
              key={p.provider_slug}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: Math.min(i * 0.01, 0.5) }}
            >
              <Card
                className={`border-[var(--qn-border)] bg-[var(--qn-card)] cursor-pointer hover:border-[var(--qn-primary)]/30 transition-all ${isSelected ? "ring-1 ring-[var(--qn-primary)]" : ""}`}
                onClick={() => fetchDetail(p.provider_slug)}
                data-testid={`health-card-${p.provider_slug}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                    <span className="text-sm font-medium truncate flex-1">{p.provider_name}</span>
                    <span className="text-[10px] font-mono text-[var(--qn-foreground-muted)]">{Math.round(p.latency_ms)}ms</span>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <Badge className="text-[9px]" style={{ background: cfg.color + "15", color: cfg.color, border: `1px solid ${cfg.color}25` }}>
                      {cfg.label}
                    </Badge>
                    <span className="text-[10px] text-[var(--qn-foreground-muted)]">
                      {p.timestamp ? new Date(p.timestamp).toLocaleTimeString() : ""}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Detail Panel + Latency Chart */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Provider Detail */}
        <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="provider-detail-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[var(--qn-primary)]" />
              {providerDetail ? providerDetail.provider_slug : "Select a Provider"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {providerDetail && providerDetail.history?.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={providerDetail.history.slice(0, 50).reverse()} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                  <defs>
                    <linearGradient id="detGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="timestamp" tick={false} />
                  <YAxis tick={{ fontSize: 10, fill: "var(--qn-foreground-muted)" }} />
                  <RechartsTooltip
                    contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11, color: "var(--qn-foreground)" }}
                    wrapperStyle={{ outline: "none" }}
                    labelFormatter={(v) => new Date(v).toLocaleString()}
                  />
                  <Area type="monotone" dataKey="latency_ms" stroke="#3b82f6" fill="url(#detGrad)" strokeWidth={1.5} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-[var(--qn-foreground-muted)] text-sm">
                Click a provider card to see latency history
              </div>
            )}
            {providerDetail?.incidents?.length > 0 && (
              <div className="mt-3 space-y-1">
                <div className="text-xs font-medium text-[var(--qn-foreground-muted)]">Recent Incidents</div>
                {providerDetail.incidents.slice(0, 3).map(inc => (
                  <div key={inc.incident_id} className="flex items-center gap-2 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: inc.resolved_at ? "#6b7280" : "#ef4444" }} />
                    <span>{inc.severity}</span>
                    <span className="text-[var(--qn-foreground-muted)]">{new Date(inc.started_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Latency Ranking */}
        <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="latency-ranking">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              Fastest Providers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={latency.slice(0, 15)} margin={{ top: 5, right: 5, bottom: 50, left: 5 }}>
                <XAxis dataKey="provider_name" tick={{ fontSize: 9, fill: "var(--qn-foreground-muted)" }} angle={-45} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 9, fill: "var(--qn-foreground-muted)" }} />
                <RechartsTooltip contentStyle={{ background: "var(--qn-card)", border: "1px solid var(--qn-border)", borderRadius: 8, fontSize: 11, color: "var(--qn-foreground)" }} wrapperStyle={{ outline: "none" }} cursor={{ fill: "rgba(255,255,255,0.06)" }} />
                <Bar dataKey="avg_latency_ms" radius={[3, 3, 0, 0]}>
                  {latency.slice(0, 15).map((e, i) => (
                    <Cell key={i} fill={e.avg_latency_ms < 100 ? "#10b981" : e.avg_latency_ms < 200 ? "#3b82f6" : "#f59e0b"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Incidents */}
      {incidents.length > 0 && (
        <Card className="border-[var(--qn-border)] bg-[var(--qn-card)]" data-testid="recent-incidents">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-[var(--qn-primary)]" />
              Recent Incidents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {incidents.slice(0, 8).map(inc => {
                const sevColor = inc.severity === "major" ? "#ef4444" : inc.severity === "minor" ? "#f97316" : "#f59e0b";
                return (
                  <div key={inc.incident_id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--qn-surface-hover)] transition-colors text-sm">
                    <div className="w-2 h-2 rounded-full" style={{ background: inc.resolved_at ? "#6b7280" : sevColor }} />
                    <span className="font-medium w-36 truncate">{inc.provider_name}</span>
                    <Badge className="text-[9px]" style={{ background: sevColor + "15", color: sevColor }}>{inc.severity}</Badge>
                    <span className="text-xs text-[var(--qn-foreground-muted)] flex-1">{new Date(inc.started_at).toLocaleString()}</span>
                    <span className="text-[10px]">
                      {inc.resolved_at ? <span className="text-green-400">Resolved</span> : <span className="text-red-400">Active</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
